import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Boxes, CheckCircle2, ClipboardList, Globe, Gauge, ArrowRight, Database, MessageSquare } from 'lucide-react';
import { LANGUAGES } from '../lib/i18n';
import { usePageMeta } from '../hooks/usePageMeta';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { getLotImage } from '../lib/stoneImages';

export default function Landing() {
  const { t, i18n } = useTranslation();
  const { isAuthenticated } = useAuth();
  // Logged-in vendors stay inside the dashboard shell when entering the exchange.
  const exchangePath = isAuthenticated ? '/dashboard/exchange' : '/exchange';
  const [stats, setStats] = useState<{ published_count: number; seller_count: number; origin_count: number; latest_lots: any[] } | null>(null);

  usePageMeta({
    title: 'MatchMarble — Global B2B Exchange for Marble',
    description: 'Find serious buyers for your marble stock. Publish blocks and slabs to a global B2B exchange. Zero commissions.',
    canonical: 'https://matchmarble.com/',
  });

  const changeLang = (code: string) => {
    i18n.changeLanguage(code);
    localStorage.setItem('mt_lang', code);
    const expires = new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toUTCString();
    document.cookie = `mt_lang=${code};expires=${expires};path=/;SameSite=Lax`;
  };

  useEffect(() => {
    api.getPublicStats().then(setStats).catch(() => {});
  }, []);

  return (
    <div>
      {/* Hero */}
      <section className="bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-14">
          <div className="grid gap-10 lg:grid-cols-[0.9fr_1.1fr] lg:items-center">
            <div className="max-w-3xl">
              <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[1.05] text-stone-950 mb-4">
                {t('hero.title1')}<br />{t('hero.title2')}
              </h1>
              <p className="text-xl sm:text-2xl font-display text-stone-700 mb-3">{t('hero.subtitle')}</p>
              <p className="text-base text-stone-500 max-w-xl mb-8 leading-relaxed">{t('hero.desc')}</p>
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                <Link to={exchangePath} className="btn-primary gap-2 text-base px-7 py-3">
                  {t('hero.exploreExchange')} <Globe size={18} />
                </Link>
                <Link to="/login" className="btn-secondary gap-2 text-base px-7 py-3">
                  {t('hero.vendorDashboard')} <ArrowRight size={18} />
                </Link>
              </div>
            </div>
            <MarketplacePreview stats={stats} />
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl sm:text-4xl text-stone-950 mb-4">{t('howItWorks.title')}</h2>
          <p className="text-stone-500 max-w-2xl mb-12">{t('howItWorks.desc')}</p>
          <div className="grid md:grid-cols-3 gap-6">
            <StepCard index={1} icon={Database} title={t('howItWorks.step1Title')} desc={t('howItWorks.step1Desc')} />
            <StepCard index={2} icon={Globe} title={t('howItWorks.step2Title')} desc={t('howItWorks.step2Desc')} />
            <StepCard index={3} icon={MessageSquare} title={t('howItWorks.step3Title')} desc={t('howItWorks.step3Desc')} />
          </div>
        </div>
      </section>

      {/* Solution */}
      <section className="py-20 bg-stone-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl sm:text-4xl text-stone-950 mb-12">{t('solution.title')}</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <SolutionCard
              eyebrow={t('solution.forVendors', 'For vendors')}
              title={t('solution.inventoryOS')}
              desc={t('solution.level1Desc')}
              features={[t('solution.level1f1'), t('solution.level1f2'), t('solution.level1f3'), t('solution.level1f4')]}
            />
            <SolutionCard
              eyebrow={t('solution.forBuyers', 'For buyers')}
              title={t('solution.globalDiscovery')}
              desc={t('solution.level2Desc')}
              features={[t('solution.level2f1'), t('solution.level2f2'), t('solution.level2f3'), t('solution.level2f4')]}
              accent
            />
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl sm:text-4xl text-stone-950 mb-4">{t('cta.title')}</h2>
          <p className="text-stone-500 mb-8">{t('cta.desc')}</p>
          <div className="flex flex-col items-stretch justify-center gap-3 sm:flex-row sm:items-center">
            <Link to={exchangePath} className="btn-primary justify-center gap-2 text-base px-8 py-3">
              {t('cta.openExchange')} <Globe size={18} />
            </Link>
            <Link to="/login" className="btn-secondary justify-center gap-2 text-base px-8 py-3">{t('cta.vendorLogin')}</Link>
          </div>
        </div>
      </section>

      <footer className="border-t border-stone-100 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="text-xs text-stone-400">MatchMarble © {new Date().getFullYear()}</div>
          <div className="flex flex-wrap items-center justify-center gap-1">
            <Globe size={13} className="mr-1 shrink-0 text-stone-400" aria-hidden="true" />
            {LANGUAGES.map(lang => (
              <button
                key={lang.code}
                onClick={() => changeLang(lang.code)}
                aria-label={lang.label}
                title={lang.label}
                aria-pressed={i18n.language === lang.code}
                className={`px-2 py-1 text-xs rounded transition-colors ${
                  i18n.language === lang.code
                    ? 'bg-stone-900 text-white'
                    : 'text-stone-400 hover:text-stone-700 hover:bg-stone-100'
                }`}
              >
                {lang.code.toUpperCase()}
              </button>
            ))}
          </div>
        </div>
      </footer>
    </div>
  );
}

function MarketplacePreview({ stats }: { stats: { published_count: number; seller_count: number; origin_count: number; latest_lots: any[] } | null }) {
  const { isAuthenticated } = useAuth();
  const exchangePath = isAuthenticated ? '/dashboard/exchange' : '/exchange';
  const lots = stats?.latest_lots || [];

  return (
    <div className="rounded-xl border border-stone-200 bg-white shadow-2xl shadow-stone-200/60 overflow-hidden">
      <div className="border-b border-stone-100 px-4 py-3">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-stone-950 text-white">
              <ClipboardList size={16} />
            </div>
            <div>
              <div className="text-sm font-semibold text-stone-950">Marketplace control loop</div>
              <div className="text-[11px] text-stone-400">Inventory SaaS to public exchange to buyer inquiry</div>
            </div>
          </div>
          <span className="hidden rounded-md bg-green-50 px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-green-700 sm:inline-flex">
            Live data
          </span>
        </div>
      </div>

      <div className="grid gap-0 border-b border-stone-100 sm:grid-cols-3">
        <PreviewMetric icon={Globe} label="Public lots" value={stats ? String(stats.published_count) : '...'} />
        <PreviewMetric icon={Boxes} label="Suppliers" value={stats ? String(stats.seller_count) : '...'} />
        <PreviewMetric icon={Gauge} label="Origins" value={stats ? String(stats.origin_count) : '...'} />
      </div>

      <div className="grid gap-0 lg:grid-cols-[1fr_220px]">
        <div className="divide-y divide-stone-100">
          {(lots.length ? lots : fallbackLots).slice(0, 3).map((lot) => {
            const image = getLotImage(lot);
            return (
              <Link key={lot.id || lot.title} to={exchangePath} className="flex items-center justify-between gap-4 px-4 py-3 hover:bg-stone-50">
                <div className="flex min-w-0 items-center gap-3">
                  <div className="h-10 w-10 shrink-0 overflow-hidden rounded-lg bg-stone-100">
                    {image ? <img src={image} alt="" className="h-full w-full object-cover" /> : null}
                  </div>
                  <div className="min-w-0">
                    <div className="truncate text-sm font-medium text-stone-900">{lot.title}</div>
                    <div className="text-xs text-stone-400">{lot.material} · {lot.origin}</div>
                  </div>
                </div>
                <StatusPill label={lot.type || 'slab'} />
              </Link>
            );
          })}
        </div>

        <div className="border-t border-stone-100 bg-surface p-4 lg:border-l lg:border-t-0">
          <div className="text-[10px] font-semibold uppercase tracking-wider text-stone-400">Vendor workflow</div>
          <div className="mt-3 space-y-3">
            {[
              'Upload stock with photos and dimensions',
              'Publish selected lots to the exchange',
              'Manage leads, messages and reservations',
            ].map((item) => (
              <div key={item} className="flex items-start gap-2 text-xs text-stone-600">
                <CheckCircle2 size={14} className="mt-0.5 shrink-0 text-green-600" />
                <span>{item}</span>
              </div>
            ))}
          </div>
          <Link to="/login" className="mt-4 inline-flex items-center gap-1.5 text-xs font-medium text-stone-900 hover:text-accent">
            Open dashboard <ArrowRight size={13} />
          </Link>
        </div>
      </div>
    </div>
  );
}

const fallbackLots = [
  { id: 'fallback-1', title: 'Statuario Extra', material: 'Marble', origin: 'Carrara', type: 'slab', images_json: '[]' },
  { id: 'fallback-2', title: 'Calacatta Oro', material: 'Marble', origin: 'Carrara', type: 'slab', images_json: '[]' },
  { id: 'fallback-3', title: 'Bianco Carrara Block', material: 'Marble', origin: 'Carrara', type: 'block', images_json: '[]' },
];

function PreviewMetric({ icon: Icon, label, value }: { icon: any; label: string; value: string }) {
  return (
    <div className="border-b border-stone-100 px-4 py-4 sm:border-b-0 sm:border-r sm:last:border-r-0">
      <Icon size={16} className="mb-2 text-stone-400" />
      <div className="text-2xl font-semibold text-stone-950">{value}</div>
      <div className="text-[10px] uppercase tracking-wider text-stone-400">{label}</div>
    </div>
  );
}

function StatusPill({ label }: { label: string }) {
  const isBlock = label === 'block';
  return (
    <span className={`shrink-0 rounded-md px-2 py-1 text-[10px] font-semibold uppercase tracking-wider ${
      isBlock ? 'bg-amber-50 text-amber-700' : 'bg-stone-100 text-stone-600'
    }`}>
      {label}
    </span>
  );
}

function SolutionCard({ eyebrow, title, desc, features, accent }: {
  eyebrow: string; title: string; desc: string; features: string[]; accent?: boolean;
}) {
  return (
    <div className={`card p-8 ${accent ? 'ring-1 ring-accent/20' : ''}`}>
      <span className={`text-[11px] font-semibold uppercase tracking-wider ${accent ? 'text-accent' : 'text-stone-400'}`}>{eyebrow}</span>
      <h3 className="font-display text-2xl text-stone-950 mt-1.5 mb-3">{title}</h3>
      <p className="text-sm text-stone-500 mb-6 leading-relaxed">{desc}</p>
      <div className="space-y-2.5">
        {features.map(f => (
          <div key={f} className="flex items-start gap-2 text-sm text-stone-600">
            <CheckCircle2 size={16} className={`mt-0.5 shrink-0 ${accent ? 'text-accent' : 'text-stone-400'}`} />
            <span>{f}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function StepCard({ index, icon: Icon, title, desc }: { index: number; icon: any; title: string; desc: string }) {
  return (
    <div className="card p-6 transition-all duration-200 hover:shadow-card-hover hover:-translate-y-0.5">
      <div className="mb-4 flex items-center justify-between">
        <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-stone-950 text-white">
          <Icon size={20} />
        </div>
        <span className="font-display text-4xl leading-none text-stone-200 select-none">{String(index).padStart(2, '0')}</span>
      </div>
      <h3 className="text-lg font-medium text-stone-900 mb-2">{title}</h3>
      <p className="text-sm text-stone-500 leading-relaxed">{desc}</p>
    </div>
  );
}
